// Clean shortcut routes for Endpoints/Admin plus legacy Settings URLs.
//
// The app is otherwise driven by query params on the single `/` route
// (?mode=<mode>&adminView=<id>&…), which makes for long, opaque share
// links. These routes let you hand someone a readable path and redirect
// it to the canonical query-param URL. It is a pure
// entry point: once redirected, every downstream routing/nav path is
// untouched.
//
// `/settings/*` remains a compatibility entry point. New readable routes
// use `/endpoints/*`, `/governance/*`, and `/admin/*`, with small aliases for
// user-facing names that differ from stable internal view ids.
//
// The redirect goes through react-router's own <Navigate>, so its
// internal location stays consistent — no replaceState behind its back,
// nothing for the app's URL writers to clobber.

import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { ADMIN_VIEWS, firstViewForMode, modeForView } from "./AdminMenu.jsx";

const VIEW_ALIASES = {
    endpoints: {
        overview: "endpoints",
        connect: "connect",
        configuration: "endpoint-config",
        tokens: "tokens",
        installers: "installers",
    },
    admin: {
        access: "users",
        identity: "directory",
        "data-sources": "data-sources",
        "ai-governance": "ai-services",
        "ai-services": "ai-services",
    },
    governance: {
        policy: "ai-services",
        "ai-governance": "ai-services",
        "ai-services": "ai-services",
    },
};

export function resolveShortcut(mode, requestedView) {
    const requested = (requestedView || "").toLowerCase();

    if (mode) {
        const resolved = VIEW_ALIASES[mode]?.[requested] || requested;
        // AI Governance used to be an Admin child. Keep both readable Admin
        // aliases working while promoting the destination to its own mode.
        if (mode === "admin" && resolved === "ai-services") {
            return { mode: "governance", view: "ai-services" };
        }
        if (ADMIN_VIEWS.some((candidate) => candidate.id === resolved && candidate.mode === mode)) {
            return { mode, view: resolved };
        }
        return { mode, view: firstViewForMode(mode) };
    }

    // Legacy /settings/<old-id> URLs follow the view to its new owner.
    const resolved = ADMIN_VIEWS.some((candidate) => candidate.id === requested)
        ? requested
        : firstViewForMode("admin");
    return { mode: modeForView(resolved) || "admin", view: resolved };
}

export default function SettingsRedirect({ mode = null, fixedView = null }) {
    const { view } = useParams();
    const [params] = useSearchParams();

    const resolved = resolveShortcut(mode, fixedView || view);

    // Preserve any existing query params, drop the legacy single-source
    // ones that fight mode-aware routing, then set canonical state.
    const next = new URLSearchParams(params);
    next.delete("view");
    next.delete("menu");
    next.delete("settingsView");
    next.set("mode", resolved.mode);
    next.set("adminView", resolved.view);

    return <Navigate to={{ pathname: "/", search: `?${next.toString()}` }} replace />;
}
